import { useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { Button, Badge, Progress } from '@/components/ui'
import { useWorkflowRunDetail, useCancelWorkflowRun, useRetryNode, useSkipNode, useProject, useAuth, useWorkflowRunStream, useApprovePhase, useRejectPhase, useNowTick } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { extractValidationMessage } from '@/lib/validationErrors'
import { getErrorMessage } from '@/api/errors'
import { formatDurationMs, formatTimestampShort } from '@/lib/timeFormatters'
import { canManageWorkflowRun, ChainBadge, LinkedEntityBadge, WorkflowStatusBadge } from '@/components/projects/workflows/shared'
import { PipelineDiagram, CancelRunDialog, WorkflowRunsErrorState } from '@/components/projects/workflows/runs'
import { AgentRunSummaryBanner } from '@/components/projects/workflows/runs/AgentRunSummaryBanner'
import { HaltedRunBanner } from '@/components/projects/workflows/runs/HaltedRunBanner'
import { RunFailureBanner } from '@/components/projects/workflows/runs/RunFailureBanner'
import { WorkflowErrorCodes } from '@/lib/workflowErrorCodes'
import { NodeDetailPanel } from '@/components/projects/workflows/nodes/NodeDetailPanel'
import { RetryNodeDialog } from '@/components/projects/workflows/nodes/RetryNodeDialog'
import { SkipNodeDialog } from '@/components/projects/workflows/nodes/SkipNodeDialog'
import { ArrowLeft, XCircle, Loader2, Wifi, WifiOff, MessageSquare } from 'lucide-react'
import { isActiveRunStatus, isCancellableRunStatus, getPhaseEffectiveStatus } from '@/types'
import type { WorkflowRunNode } from '@/types'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

export function WorkflowRunDetailPage() {
  const { projectId, runId } = useParams()
  const navigate = useNavigate()
  const { hasPermission, isLoading: authLoading, user, isAdmin } = useAuth(projectId)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [retryDialogNode, setRetryDialogNode] = useState<WorkflowRunNode | null>(null)
  const [skipDialogNode, setSkipDialogNode] = useState<WorkflowRunNode | null>(null)
  const [streamReconnectKey, setStreamReconnectKey] = useState(0)

  const { data: queryRun, isLoading, isError, refetch, isFetchedAfterMount } = useWorkflowRunDetail(projectId!, runId)
  const { data: project } = useProject(projectId)
  const cancelRun = useCancelWorkflowRun(projectId!)
  const retryNode = useRetryNode(projectId!, runId!)
  const skipNode = useSkipNode(projectId!, runId!)
  const approvePhase = useApprovePhase(projectId!)
  const rejectPhase = useRejectPhase(projectId!)

  const run = queryRun
  const { isConnected, isTerminal, error: streamError } = useWorkflowRunStream(
    projectId!,
    runId,
    isFetchedAfterMount && !!run && isActiveRunStatus(run.status),
    streamReconnectKey,
  )
  const liveUpdates = isConnected ? 'Live' : streamError ? 'Polling' : 'Connecting'

  const isRunActive = run ? isActiveRunStatus(run.status) && !isTerminal : false
  useNowTick(isRunActive)

  if (authLoading) {
    return null
  }

  if (!hasPermission(Permissions.Project.Workflow.View, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  const canCancelPermission = hasPermission(Permissions.Project.Workflow.Cancel, projectId)
  const canIntervene = hasPermission(Permissions.Project.Workflow.Intervene, projectId)
  const canApprove = hasPermission(Permissions.Project.Workflow.Approve, projectId)

  const selectedNode: WorkflowRunNode | undefined = run?.phases
    ? run.phases.flatMap(p => p.branches).flatMap(b => b.nodes).find(n => n.id === selectedNodeId)
    : undefined

  const showCancel = canCancelPermission && !!run && isCancellableRunStatus(run.status)
  const manageAllowed = canManageWorkflowRun({ triggeredByUserId: run?.triggeredByUserId ?? null }, user?.id, isAdmin)
  const interveneAllowed = canIntervene && manageAllowed
  const canViewChat = !!run?.chatId && !!user?.id && user.id === run.triggeredByUserId
  const phaseProgress = run && run.totalPhases > 0 ? (run.completedPhases / run.totalPhases) * 100 : 0

  const handleApprove = (phaseId: string, comment?: string) => {
    approvePhase.mutate({ phaseId, comment }, {
      onSuccess: () => toast.success('Phase approved'),
      onError: (error) => toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to approve phase'),
    })
  }

  const handleReject = (phaseId: string, reason: string) => {
    rejectPhase.mutate({ phaseId, reason }, {
      onSuccess: () => toast.success('Phase rejected'),
      onError: () => toast.error('Failed to reject phase'),
    })
  }

  const handleRetry = (nodeId: string, modifiedInputsJson?: string) => {
    retryNode.mutate({ nodeId, modifiedInputsJson }, {
      onSuccess: () => {
        setStreamReconnectKey(key => key + 1)
        toast.success('Node retry initiated')
      },
      onError: (err) => toast.error(extractValidationMessage(err) ?? 'Failed to retry node'),
    })
  }

  const handleSkip = (nodeId: string) => {
    skipNode.mutate(nodeId, {
      onSuccess: () => {
        setStreamReconnectKey(key => key + 1)
        toast.success('Node skipped')
      },
      onError: (err) => toast.error(extractValidationMessage(err) ?? 'Failed to skip node'),
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite" data-testid="workflow-run-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading workflow run details" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-6">
        <WorkflowRunsErrorState onRetry={() => refetch()} />
      </div>
    )
  }

  if (!run) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Run not found.</p>
        <Button variant="ghost" className="mt-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>
    )
  }

  const effectiveRunStatus = run.phases.some(p => getPhaseEffectiveStatus(p) === 'AwaitingUserInput')
    ? 'AwaitingUserInput' as const
    : run.status

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="p-4 border-b shrink-0">
        {project && (
          <div className="text-xs text-muted-foreground mb-2">
            {project.name}
          </div>
        )}

        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/projects/${projectId}/workflows/runs`)} aria-label="Back to workflow runs">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span className="font-semibold text-lg">{run.definitionDisplayName}</span>
          <Badge variant="secondary" className="text-xs">v{run.definitionVersion}</Badge>
          <WorkflowStatusBadge status={effectiveRunStatus} />
          <div className="flex items-center gap-2 w-32">
            <Progress value={phaseProgress} className="h-2 flex-1" />
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
              {run.completedPhases}/{run.totalPhases}
            </span>
          </div>
          {isRunActive && (
            <span
              role="status"
              data-testid="live-updates"
              className="flex items-center gap-1 text-xs text-muted-foreground"
              title={streamError ?? (isConnected ? 'Live updates active' : 'Connecting to live updates')}
            >
              {isConnected ? <Wifi className="h-3 w-3 text-green-500" aria-hidden="true" /> : <WifiOff className="h-3 w-3 text-amber-500" aria-hidden="true" />}
              {liveUpdates}
            </span>
          )}
          <LinkedEntityBadge type={run.linkedEntityType} number={run.linkedEntityNumber} fallbackId={run.linkedEntityId} entityId={run.linkedEntityId} available={run.linkedEntityAvailable} status={run.linkedEntityStatus} />
          {run.chainRunId && (
            <ChainBadge
              name={run.chainDisplayName ?? 'Chain'}
              detail={run.chainLinkPosition && run.chainLinksCount ? `link ${run.chainLinkPosition}/${run.chainLinksCount}` : undefined}
              onClick={() => navigate(`/projects/${projectId}/workflows/runs/chains/${run.chainRunId}`)}
            />
          )}
          {canViewChat && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto bg-toolbar border-border-accent"
              onClick={() => navigate(`/projects/${projectId}/chat?chatId=${run.chatId}`)}
            >
              <MessageSquare className="h-3 w-3 mr-1" /> View chat
            </Button>
          )}
          {showCancel && (
            <Button size="sm" variant="outline" className={cn("bg-toolbar border-border-accent", canViewChat ? '' : 'ml-auto')} disabled={!manageAllowed} title={manageAllowed ? undefined : 'Only an administrator or the user who started the workflow can cancel it.'} onClick={() => setCancelDialogOpen(true)}>
              <XCircle className="h-3 w-3 mr-1" /> Cancel
            </Button>
          )}
        </div>

        <div className="rounded-lg border bg-card px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-y-2 text-sm text-muted-foreground">
            <span><span className="font-medium text-foreground"># Run-{run.id.slice(0, 8)}</span></span>
            <span>Created: <span className="font-medium text-foreground">{formatTimestampShort(run.createdAt)}</span></span>
            <span>Started: <span className="font-medium text-foreground">{formatTimestampShort(run.startedAt)}</span></span>
            <span>Ended: <span className="font-medium text-foreground">{formatTimestampShort(run.completedAt)}</span></span>
            <span>Duration: <span className="font-medium text-foreground">{formatDurationMs(run.durationMs)}</span></span>
            <span>Lead time: <span className="font-medium text-foreground">{formatDurationMs(run.leadTimeMs)}</span></span>
            <span>User: <span className="font-medium text-foreground">{run.triggeredBy}</span></span>
            <span className="font-medium text-foreground">Via {run.triggeredFrom}</span>
          </div>
        </div>

        {run.agentSummary && <AgentRunSummaryBanner summary={run.agentSummary} />}
        {run.status === 'Failed' && (() => {
          for (let pi = 0; pi < run.phases.length; pi++) {
            const phase = run.phases[pi]
            for (const branch of phase.branches) {
              const nodeIdx = branch.nodes.findIndex(
                n => n.errorCode === WorkflowErrorCodes.TokenExhausted
                  || n.errorCode === WorkflowErrorCodes.ConnectorExpired
                  || n.errorCode === WorkflowErrorCodes.TitanUnavailable
              )
              if (nodeIdx !== -1) {
                const node = branch.nodes[nodeIdx]
                const downstreamSteps = [
                  ...branch.nodes.slice(nodeIdx + 1).map(n => n.name),
                  ...run.phases.slice(pi + 1).flatMap(p => p.branches.flatMap(b => b.nodes.map(n => n.name))),
                ]
                return (
                  <HaltedRunBanner
                    errorCode={node.errorCode!}
                    phaseName={phase.name}
                    stepName={node.name}
                    errorMessage={node.errorMessage}
                    downstreamSteps={downstreamSteps}
                    currentSpendUsd={run.currentSpendUsd}
                    capUsd={run.capUsd}
                  />
                )
              }
            }
          }

          for (const phase of run.phases) {
            for (const branch of phase.branches) {
              const skillNode = branch.nodes.find(
                n => n.errorCode === WorkflowErrorCodes.AgentSkillUnresolved && n.errorMessage
              )
              if (skillNode) {
                return (
                  <RunFailureBanner
                    phaseName={phase.name}
                    stepName={skillNode.name}
                    message={skillNode.errorMessage!}
                  />
                )
              }
            }
          }

          return null
        })()}
      </div>


      <div className="flex flex-1 min-h-0 p-4 gap-4">
        <div className="w-[460px] shrink-0 flex flex-col rounded-lg border bg-card">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 pt-4 pb-2">
            Phases
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-4">
            <PipelineDiagram
              run={run}
              selectedNodeId={selectedNodeId}
              onSelectNode={setSelectedNodeId}
              canApprove={canApprove}
              onApprove={handleApprove}
              onReject={handleReject}
              isApproving={approvePhase.isPending}
              isRejecting={rejectPhase.isPending}
            />
          </div>
        </div>

        {selectedNode && (
          <div className="flex-1 min-w-0 flex flex-col">
            <NodeDetailPanel
              node={selectedNode}
              projectId={projectId!}
              canIntervene={interveneAllowed}
              onClose={() => setSelectedNodeId(null)}
              onRetry={(nodeId) => {
                if (!interveneAllowed || !run?.phases) return
                const node = run.phases.flatMap(p => p.branches).flatMap(b => b.nodes).find(n => n.id === nodeId)
                if (node) setRetryDialogNode(node)
              }}
              onSkip={(nodeId) => {
                if (!interveneAllowed || !run?.phases) return
                const node = run.phases.flatMap(p => p.branches).flatMap(b => b.nodes).find(n => n.id === nodeId)
                if (node) setSkipDialogNode(node)
              }}
            />
          </div>
        )}
      </div>

      <CancelRunDialog
        run={run}
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={(id) => cancelRun.mutate(id)}
      />

      <RetryNodeDialog
        key={retryDialogNode?.id}
        node={retryDialogNode}
        open={!!retryDialogNode}
        onOpenChange={(open) => { if (!open) setRetryDialogNode(null) }}
        onConfirm={handleRetry}
        isPending={retryNode.isPending}
      />

      <SkipNodeDialog
        node={skipDialogNode}
        open={!!skipDialogNode}
        onOpenChange={(open) => { if (!open) setSkipDialogNode(null) }}
        onConfirm={handleSkip}
        isPending={skipNode.isPending}
      />
    </div>
  )
}
